import React, { useRef, useState } from 'react';
import { Upload, X, CheckCircle2, AlertCircle } from 'lucide-react';

interface FileUploadProps {
  label?: string;
  accept?: string;
  maxSize?: number; // in MB
  onFileSelect: (file: File | null) => void;
  error?: string;
  helperText?: string;
  className?: string;
  required?: boolean;
}

export const FileUpload = ({
  label,
  accept,
  maxSize = 2,
  onFileSelect,
  error,
  helperText,
  className = '',
  required = false
}: FileUploadProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    // Basic validation
    if (maxSize && file.size > maxSize * 1024 * 1024) {
      onFileSelect(null);
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    onFileSelect(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={`flex flex-col gap-1 w-full ${className}`}> {/* Reduced gap-2 to gap-1 */}
      {label && (
        <label className="text-xs font-bold text-slate-700 px-1"> {/* Reduced text-sm to text-xs */}
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative group transition-all duration-300
          ${selectedFile ? 'h-20' : 'h-24'} /* Dropped another 16px off the height floor */
          rounded-xl border-2 border-dashed
          flex flex-col items-center justify-center p-2 text-center /* Reduced padding from p-3 to p-2 */
          ${dragActive ? 'border-blue-500 bg-blue-50/50 scale-[0.99]' : 'border-slate-200 bg-slate-50/30 hover:bg-slate-50 hover:border-slate-300'}
          ${error ? 'border-red-500 bg-red-50/30' : ''}
          ${selectedFile ? 'border-[#8dc63f] bg-[#8dc63f]/10/30' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
        />

        {selectedFile ? (
          <div className="flex flex-col items-center gap-0.5 animate-in zoom-in-95 duration-300">
            <div className="bg-white rounded-lg shadow-sm border border-[#8dc63f]/50 p-0.5">
              <CheckCircle2 className="w-5 h-5 text-[#8dc63f]" /> {/* Reduced to w-5 */}
            </div>
            <div className="space-y-0">
              <p className="text-xs font-bold text-slate-900 truncate max-w-[200px]">{selectedFile.name}</p>
              <p className="text-[10px] font-medium text-slate-400">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); removeFile(); }}
              className="absolute top-1.5 right-1.5 p-1 bg-white shadow-sm border border-slate-100 rounded-full text-slate-400 hover:text-rose-500 hover:border-rose-100 transition-all z-20"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          /* Using an explicit layout structure to save space */
          <div className="flex flex-col items-center gap-1">
            <Upload className="w-5 h-5 text-blue-500" /> {/* Dropped wrapper div completely to save height */}
            <div className="space-y-0">
              <p className="text-xs font-bold text-slate-900 tracking-tight">
                Click to upload or drag and drop
              </p>
              <p className="text-[10px] font-medium text-slate-400">
                {helperText || `Only .csv files accepted`}
              </p>
            </div>
          </div>
        )}
      </div>

      {error ? (
        <p className="text-xs font-semibold text-red-500 px-1 flex items-center gap-1.5 mt-0.5">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </p>
      ) : null}
    </div>
  );
};



